
import {map} from 'rxjs/operators';
import { ErrorHandler, Injectable } from '@angular/core';
import { AuthService } from '../../auth/auth/auth.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Business } from '../../business/models/business.model';
import { Airport } from '../../airport/models/airport.model';
import { UserService } from './user.service';
import { UserInfo } from '../models/user-info.model';
import {AuthEventType, IAuthEvent} from '../../auth/models/auth-event.model';
import {NewBusiness} from '../../business/models/new-business-model';


@Injectable()
export class BusinessManagerService {
  userInfo: UserInfo;

  constructor(private _http: HttpClient,
              private _auth: AuthService,
              private _errorHandler: ErrorHandler,
              private userService: UserService) {
    this._auth.authStream.subscribe(e => this._onAuthEvent(e));
  }

  private async _onAuthEvent(e: IAuthEvent): Promise<void> {
    if (!e) {
      return;
    }
    switch (e.type) {
      case AuthEventType.SignIn:
        this.userInfo = await this.userService.getUserInfo();
        break;
      case AuthEventType.InvalidSignIn:
      case AuthEventType.SignOut:
        this.userInfo = null;
        break;
    }
  }

  /**
   * Method creates a new business in the API Server with data from the NewBusinessInfo object passed in.
   * requires that user is authenticated, and is a flight-deck admin.
   * @param {NewBusinessInfo} {business} data sent to the server to make a new Business
   * @returns {Promise<Business>} API Server will respond with a new Business OBJ.
   */
  async createBusiness(business: NewBusiness): Promise<Business> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/`;
    try {
      const RESPONSE = await this._http.post(URL, business.serialize()).toPromise();
      const BUSINESS = Business.parse(RESPONSE);
      if (!BUSINESS) {
        const ERROR = new Error('API did not return valid business object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        await this._auth.login();
        this.userInfo = await this.userService.getUserInfo();
        return BUSINESS;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * sends put request to api server to update the given business.
   * Method updates the API Server with data from the business object passed in.
   * Requires that user is authenticated.
   * @param {Business} {Business} will be serialized into JSON and sent to server.
   * @returns {Promise<Business>} API Server will respond with updated Business OBJ.
   */
  async updateBusiness(business: Business): Promise<Business> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser || !await this.userOwnsBusiness(business.id)) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${business.id}`;
    try {
      const RESPONSE = await this._http.put(URL, business.serialize()).toPromise();
      const BUSINESS = Business.parse(RESPONSE);
      if (!BUSINESS) {
        const ERROR = new Error('API did not return valid business object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        return BUSINESS;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * method stub for the API call to delete a business
   * @param {Business} {Business} will be serialized into JSON and sent to server.
   * @returns {Promise<boolean>} Returns true if the delete is successful, and false otherwise.
   */
  async deleteBusiness(business: Business): Promise<boolean> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser || !await this.isFlightDeckAdmin) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${business.id}`;
    try {
      const RESPONSE = await this._http.delete(URL).toPromise();
      return true;
    } catch (e) {
      this._errorHandler.handleError(e);
      return false;
    }
  }

  /**
   * method to call API to add a business manager
   * @param {Business} {business} the business to add the admin to.
   * @param {number} {recid} recid to be added as an admin.
   * @returns {Promise<string>} Returns SUCCESS or error message for display to user.
   */
  async addBusinessAdmin(business: Business, recid: number): Promise<string> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser || !await this.isFlightDeckAdmin) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const paddedRecid = (recid + '').padStart(8, '0');
    const newAdmin = {
      recId: paddedRecid
    };
    const URL = `${environment.airportsApiUrl}/business/${business.id}/admin`;
    try {
      const RESPONSE = await this._http.post(URL, newAdmin).toPromise();
      return 'SUCCESS';
    } catch (e) {
      this._errorHandler.handleError(e);
      return e.error.message.toString();
    }
  }

  /**
   * method to call API to add a business manager
   * @param {Business} {business} the business to add the admin to.
   * @param {number} {recid} recid to be added as an admin.
   * @returns {Promise<string>} Returns SUCCESS or error message for display to user.
   */
  async updateBusinessAdmin(business: Business, recid: number, primaryPoc: boolean, fboFeePoc: boolean): Promise<string> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser || !await this.isFlightDeckAdmin) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const paddedRecid = (recid + '').padStart(8, '0');
    const newAdmin = {
      recId: paddedRecid,
      primaryPoc: primaryPoc,
      fboFeePoc: fboFeePoc
    };
    const URL = `${environment.airportsApiUrl}/business/${business.id}/admin`;
    try {
      const RESPONSE = await this._http.put(URL, newAdmin).toPromise();
      return 'SUCCESS';
    } catch (e) {
      this._errorHandler.handleError(e);
      return e.error.message.toString();
    }
  }

  /**
   * method to call API to delete a business manager from a business
   * @param {Business} {business} the business to remove the admin from.
   * @param {number} {recid} recid to be deleted as an admin of this business.
   * @returns {Promise<string>} Returns SUCCESS or error message for display to user.
   */
  async deleteBusinessAdmin(business: Business, recid: number): Promise<string> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser || !await this.isFlightDeckAdmin) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const paddedRecid = (recid + '').padStart(8, '0');
    const URL = `${environment.airportsApiUrl}/business/${business.id}/admin/${paddedRecid}`;
    try {
      const RESPONSE = await this._http.delete(URL).toPromise();
      return 'SUCCESS';
    } catch (e) {
      this._errorHandler.handleError(e);
      return e.error.message.toString();
    }
  }

  /**
   * method to call API to add a business manager
   * @param {Airport} airport the business to add the admin to.
   * @param {number} recid to be added as an admin.
   * @returns {Promise<string>} Returns SUCCESS or error message for display to user.
   */
  async addAirportManager(airport: Airport, recid: number): Promise<string> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser || !await this.isFlightDeckAdmin) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const paddedRecid = (recid + '').padStart(8, '0');
    const newAdmin = {
      recId: paddedRecid
    };
    const URL = `${environment.airportsApiUrl}/airports/${airport.airportId}/admin`;
    try {
      const RESPONSE = await this._http.post(URL, newAdmin).toPromise();
      return 'SUCCESS';
    } catch (e) {
      this._errorHandler.handleError(e);
      return e.error.message.toString();
    }
  }

  /**
   * method to call API to delete a business manager from a business
   * @param {Airport} airport the business to remove the admin from.
   * @param {number} recid to be deleted as an admin of this business.
   * @returns {Promise<string>} Returns SUCCESS or error message for display to user.
   */
  async deleteAirportManager(airport: Airport, recid: number): Promise<string> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser || !await this.isFlightDeckAdmin) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const paddedRecid = (recid + '').padStart(8, '0');
    const URL = `${environment.airportsApiUrl}/airports/${airport.airportId}/admin/${paddedRecid}`;
    try {
      const RESPONSE = await this._http.delete(URL).toPromise();
      return 'SUCCESS';
    } catch (e) {
      this._errorHandler.handleError(e);
      return e.error.message.toString();
    }
  }

  /**
   * method stub for the API call to add an associated airport to a business, must be authenticated to work
   * @param {Business} {business} Business to add the airport to
   * @param {Number} {airportId} ID of airport to be added to the business
   * @returns {Promise<Business>} Returns the updated Business if successful, and null otherwise
   */
  async addAssociatedAirport(business: Business, airportId: Number): Promise<string> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const newAirport = {
      airportId: airportId
    };
    const URL = `${environment.airportsApiUrl}/business/${business.id}/airport`;
    try {
      const RESPONSE = await this._http.post(URL, newAirport).toPromise();
      return RESPONSE.toString();
    } catch (e) {
      this._errorHandler.handleError(e);
      return e.error.message.toString();
    }
  }

  /**
   * method stub for the API call to remove an associated airport from a business, must be authenticated to work
   * @param {Business} {business} Business to add the airport to
   * @param {Number} {airportId} ID of airport to be removed from the business
   * @returns {Promise<Business>} Returns the updated Business if successful, and null otherwise
   */
  async removeAssociatedAirport(business: Business, airportId: Number): Promise<string> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${business.id}/airport/${airportId}`;
    try {
      const RESPONSE = await this._http.delete(URL).toPromise();
      return RESPONSE.toString();
    } catch (e) {
      this._errorHandler.handleError(e);
      return e.error.message.toString();
    }
  }

  /**
   * Api Call to update the flight school object, must be authenticated to work
   * @param {Business} business the business object that will be updated
   * @returns {Observable<FlightSchool>} Returns the Flight School if successful, null otherwise
   */
  updateFlightSchool(business: Business) {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${business.id}/flightSchool`;
    const headers = new HttpHeaders({'Content-Type': 'application/json'});
    try {
      return this._http.put(URL, business.serializeFlightSchool(), {headers}).pipe(map(res => res));
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Checks business id and if current user owns that business.
   * @param businessId id of business to check.
   * {Promise<boolean>} returns true if user is Business Owner or Flight Deck Admin
   */
  async userOwnsBusiness(businessId: number): Promise<boolean> {
    // local variable isBusinessManager is set true if user is a business manager and owns the business with the id passed in.
    if (!this.userInfo) {
      this.userInfo = await this.userService.getUserInfo();
    }
    let isBusinessManager = false;
    if (businessId) {
      isBusinessManager = (this.userInfo && this.userInfo.businessesManaged && this.userInfo.businessesManaged.indexOf(businessId) !== -1);
    }

    return isBusinessManager || await this.isFlightDeckAdmin();
  }

  /**
   * Checks business id and if current user owns that business.
   * @param airportId id of business to check.
   * {Promise<boolean>} returns true if user is Business Owner or Flight Deck Admin
   */
  async userManagesAirport(airportId: number): Promise<boolean> {
    // local variable isBusinessManager is set true if user is a business manager and owns the business with the id passed in.
    if (!this.userInfo) {
      this.userInfo = await this.userService.getUserInfo();
    }
    let isAirportManager = false;
    if (airportId) {
      isAirportManager = (this.userInfo && this.userInfo.airportsManaged && this.userInfo.airportsManaged.indexOf(airportId) !== -1);
    }

    return isAirportManager || await this.isFlightDeckAdmin();
  }

  /**
   * Returns true if the current user is a flight deck admin, and false otherwise
   * **/
  async isFlightDeckAdmin(): Promise<boolean> {
    if (!this.userInfo) {
      this.userInfo = await this.userService.getUserInfo();
    }
    let flightDeckAdmin = false;
    if (this.userInfo &&
      this.userInfo.relevantAceList &&
      this.userInfo.relevantAceList.length > 0) {
      this.userInfo.relevantAceList.forEach(ace => {
        if (ace === 'flightdeckadmin') {
          flightDeckAdmin = true;
        }
      });
    }
    return flightDeckAdmin;
  }

  /**
   * Returns true if the current user is a ycf ambassador, and false otherwise
   * **/
  async isAmbassador(): Promise<boolean> {
    if (!this.userInfo) {
      this.userInfo = await this.userService.getUserInfo();
    }
    let ambassador = false;
    if (this.userInfo &&
      this.userInfo.relevantAceList &&
      this.userInfo.relevantAceList.length > 0) {
      this.userInfo.relevantAceList.forEach(ace => {
        if (ace === 'ycfambassador') {
          ambassador = true;
        }
      });
    }
    return ambassador;
  }

  /**
   * Returns true if the current user is a regional manager, and false otherwise
   * **/
  async isRegionalManager(): Promise<boolean> {
    if (!this.userInfo) {
      this.userInfo = await this.userService.getUserInfo();
    }
    let regionalManager = false;
    if (this.userInfo &&
      this.userInfo.relevantAceList &&
      this.userInfo.relevantAceList.length > 0) {
      this.userInfo.relevantAceList.forEach(ace => {
        if (ace === 'regionalmanager') {
          regionalManager = true;
        }
      });
    }
    return regionalManager;
  }

  async isSubscribedToCaribbeanGuide(): Promise<boolean> {
    if (!this.userInfo) {
      this.userInfo = await this.userService.getUserInfo();
    }
    let caribbeanGuideSubscriber = false;
    if (this.userInfo &&
      this.userInfo.relevantAceList &&
      this.userInfo.relevantAceList.length > 0) {
      this.userInfo.relevantAceList.forEach(ace => {
        if (ace === 'caribbeanguide') {
          caribbeanGuideSubscriber = true;
        }
      });
    }
    return caribbeanGuideSubscriber;
  }

  async isSubscribedToBahamasGuide(): Promise<boolean> {
    if (!this.userInfo) {
      this.userInfo = await this.userService.getUserInfo();
    }
    let bahamasGuideSubscriber = false;
    if (this.userInfo &&
      this.userInfo.relevantAceList &&
      this.userInfo.relevantAceList.length > 0) {
      this.userInfo.relevantAceList.forEach(ace => {
        if (ace === 'bahamasguide') {
          bahamasGuideSubscriber = true;
        }
      });
    }
    return bahamasGuideSubscriber;
  }

  /**
   * Checks eventId and see if current user owns that event.
   * @param eventId id of event to check.
   * {Promise<boolean>} returns true if user is event Owner or Flight Deck Admin
   */
  async userOwnsEvent(eventRecid: number): Promise<boolean> {
    // local variable isEventOwner is set true if user created the event passed in.
    if (!this.userInfo) {
      this.userInfo = await this.userService.getUserInfo();
    }
    let isEventOwner = false;
    if (this.userInfo && eventRecid && eventRecid === +this.userInfo.masterCustomerId) {
      isEventOwner = true;
    }

    return isEventOwner || await this.isFlightDeckAdmin();
  }

  /**
   * Returns true if the current user is an AOPA Member, and false otherwise
   * **/
  async isMember(): Promise<boolean> {
    if (!this.userInfo) {
      this.userInfo = await this.userService.getUserInfo();
    }
    let member = false;
    if (this.userInfo &&
      this.userInfo.relevantAceList &&
      this.userInfo.relevantAceList.length > 0) {
      this.userInfo.relevantAceList.forEach(ace => {
        if (ace === 'member') {
          member = true;
        }
      });
    }
    return member;
  }
}
